<script>
import { mapGetters } from "vuex";
import { theme } from "@/services/config";
import leave from "@/mixins/leave";

import fileUploadConfigService from "@/services/fileupload-config";
import segmentService from "@/services/segment";
import indicationService from "@/services/indication";
import { s3Url } from "@/services/config";

import Spinner from "@/components/ui/Spinner";
import FileUpload from "@/components/ui/FileUpload";
import InputTag from "@/components/ui/InputTag";
import DiagnosisSelect from "@/components/ui/DiagnosisSelect";
import MedicationSelect from "@/components/ui/MedicationSelect.vue";
import FinanciadorSelect from "@/components/ui/FinanciadorSelect.vue";
import {
  INDICATIONS_TYPES,
  RECETARIO_SOLIDARIO_DISABLED_FIELDS,
  getIndicationForm,
} from "@/services/constants";

export default {
  name: "ModalIndication",
  mixins: [leave],

  components: {
    Spinner,
    FileUpload,
    InputTag,
    DiagnosisSelect,
    MedicationSelect,
    FinanciadorSelect,
  },

  props: {
    showDialogModal: {
      type: Boolean,
    },
    currentIndication: {
      type: Object,
    },
    isModal: {
      type: Boolean,
      default: true,
    },
  },

  watch: {
    "indication.type": {
      immediate: true,
      handler(newType) {
        this.indicationForm = getIndicationForm(newType);

        if (
          newType === "medications" &&
          (this.indication.quantity === "" ||
            this.indication.quantity === null ||
            this.indication.quantity === undefined)
        ) {
          this.$set(this.indication, "quantity", "1");
        }
      },
    },
  },

  data() {
    return {
      theme,
      isLoading: false,
      isPosting: false,
      indicationsTypes: INDICATIONS_TYPES,
      indicationForm: [],
      indicationId: null,
      loadedMedications: [],
      indication: {
        type: "",
        title: "",
        medication: "",
        description: "",
        dosage: "",
        quantity: "",
        posology: "",
        quantityPosdated: "",
        dayPosdated: "",
        datePosdated: "",
        doctorId: "",
        patientId: this.currentIndication.patient._id,
        files: [],
        diagnosis: null,
        generic: "",
        patientEmail: this.currentIndication?.patient?.email || "",
        patientSocioNumber:
          this.currentIndication?.patient?.medicalInsuranceObj?.number || "",
        patientDni: this.currentIndication?.patient?.governmentId?.number || "",
        patientFinanciadorName: "",
        recetarioSolidario: false,
        patientPlan:
          this.currentIndication?.patient?.medicalInsuranceObj?.plan || "",
      },
      hasFiles: false,
      isFetchingIndication: false,
      isNewIndication: this.currentIndication.modalType === "create-indication",
      isShowed: this.showDialogModal,
      usesExternalMedication: true,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor"]),

    patientInsuranceName() {
      return (
        this.currentIndication?.patient?.medicalInsuranceObj?.publicInsurance
          ?.name || ""
      );
    },

    uploadMediaEndpoint() {
      return fileUploadConfigService.getIndicationFilesEndpoint(
        this.indicationId
      );
    },

    indicationFiles() {
      const { files = [] } = this.indication;
      files.sort((a, b) => {
        a.mimeType < b.mimeType;
      });
      files.forEach(function (file) {
        file.url = s3Url + "/" + file.key;
      });
      return files;
    },
  },

  created() {
    if (!this.isNewIndication) {
      this.indication = this.currentIndication;
    }
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode == 27) this.close();
    },

    submit() {
      if (this.indication.type === "medications" && this.hasInvalidQuantity()) {
        this.$message.error("La cantidad debe ser mayor a 0.");
        return;
      }

      if (
        this.indication.type === "medications" &&
        this.hasInvalidMedicationSelection()
      ) {
        this.$message.error("Debe seleccionar un medicamento de la lista.");
        return;
      }

      this.createIndication();
    },

    isDisabledByRecetarioSolidario(label) {
      return (
        !!this.indication.recetarioSolidario &&
        RECETARIO_SOLIDARIO_DISABLED_FIELDS.includes(label)
      );
    },

    hasInvalidMedicationSelection() {
      const medicationId = String(this.indication?.medication || "").trim();

      if (!medicationId) return true;

      return !this.$store.state.cacheMedications?.[medicationId];
    },

    setMedication(id) {
      const selected = this.loadedMedications.find((m) => m._id === id);
      this.indication.publicMedication = {
        drugId: selected?._id || "",
        name: selected?.name || "",
        brandName: selected?.brandName || "",
        drug: selected?.drug || "",
        presentation: selected?.presentation || "",
        isPsychotropic: selected?.isPsychotropic ?? false,
      };
    },

    createIndication() {
      this.isPosting = true;
      this.showLeaveGuard = false;
      const eventName = "Indication Created";
      segmentService.track({ name: eventName });

      const formData = new FormData();

      for (const key in this.indication) {
        if (["replaceable", "isLongTerm", "files", "medication"].includes(key))
          continue;
        const val = this.indication[key];
        if (key === "diagnosis") {
          if (val && typeof val === "object") {
            // 👇 Mandamos estructura igual que publicMedication
            formData.append(
              "diagnosis",
              JSON.stringify({
                id: val._id,
                name: val.description || val.name || "",
              })
            );
          } else if (val) {
            // por compatibilidad si llega como string
            formData.append(
              "diagnosis",
              JSON.stringify({
                id: val,
                name: "",
              })
            );
          }
          continue;
        }
        if (key === "publicMedication") {
          formData.append("publicMedication", JSON.stringify(val));
          continue;
        }
        if (key === "generic") {
          const parsed = val === "Sí" ? true : false;
          formData.append("generic", parsed);
          continue;
        }
        if (val instanceof File) {
          formData.append("files", val);
        } else if (val != null && val !== "") {
          formData.append(key, val);
        }
      }

      indicationService
        .createIndication(formData)
        .then((indication) => {
          this.indicationId = indication._id;
          if (this.hasFiles) {
            this.handleUploadFiles();
          } else {
            this.close();
          }
          indication.doctor = this.$store.state.user;
          this.$bus.$emit("indication-created", indication);
        })
        .catch(() => {
          this.isPosting = false;
        });
    },

    handleUploadFiles() {
      this.$refs.fileUpload && this.$refs.fileUpload.startUpload();
    },

    getFileName(key = "") {
      return key.split("/").pop();
    },

    async onSignatureChange(event) {
      const file = event.target.files[0];

      this.indication.privateKey = file;
    },

    getDropdownOptions() {
      return {
        method: "put",
        uploadMultiple: true,
        maxFiles: 8,
        maxFilesize: 5,
        acceptedFiles: "image/*,application/pdf",
      };
    },

    setDiagnosis(diagnosis) {
      this.indication.diagnosis = diagnosis;
    },

    isQuantityField(data) {
      const fieldName = String(data?.label || data?.friendlyName || "")
        .trim()
        .toLowerCase();

      return fieldName === "quantity" || fieldName === "cantidad";
    },

    hasInvalidQuantity() {
      return !/^[1-9]\d*$/.test(String(this.indication.quantity || "").trim());
    },

    preventInvalidQuantityKey(event) {
      if (["-", "+", "e", "E", ".", ","].includes(event.key)) {
        event.preventDefault();
      }
    },

    uploadFilesSuccess() {
      // REVISAR
      this.$router.back();
    },

    uploadFilesError() {
      // REVISAR
      //this.isPostingPatient = false;
    },

    onFileAdded() {
      this.hasFiles = true;
    },

    onFileRemoved() {
      this.hasFiles = false;
    },

    close() {
      this.isPosting = false;
      this.isShowed = false;
      this.$emit("close");
    },
  },
};
</script>

<template lang="pug">
ValidationObserver(v-slot="{ handleSubmit }")
  form(
    @submit.prevent="handleSubmit(submit)",
    v-if="isNewIndication",
    :class="isModal && 'modal'"
  )
    header.modal__header
      h2.modal__title Indicacion {{ indication.title }}
      .modal__actions
        el-button(type="info", @click="close()") Cancelar
        el-button.border(
          type="primary",
          native-type="submit",
          :loading="isPosting"
        ) Guardar
    .modal__content(v-if="isFetchingIndication")
      spinner
    .modal__content
      .modal__block
        .modal__section
          .modal__sign.sign(v-if="isModal")
            .sign__icon
              micon(name="assignment")
            h3.sign__title Detalle de la indicación
          article(:class="isModal && 'modal__fields'")
            .modal__row
              fieldset.modal__field
                label.label * Tipo
                ValidationProvider(
                  name="Tipo",
                  rules="required",
                  v-slot="{ errors }"
                )
                  el-select(v-model="indication.type")
                    el-option(
                      v-for="(type, typeKey) in indicationsTypes",
                      v-if="type.type !== 'diagnosis'",
                      :value="type.type",
                      :label="type.name",
                      :key="typeKey"
                    )
                  span.has-error {{ errors[0] }}
            .modal__row(v-if="indication.type !== 'medications'")
              fieldset.modal__field
                label.label Título
                  el-input(v-model="indication.title")
            .modal__row
              fieldset.modal__field
                label.label Descripción (opcional)
                  el-input(v-model="indication.description")
            .modal__row(v-for="(data, index) in indicationForm", :key="index")
              
              fieldset.modal__field(v-if="data.inputType === 'medicationSelect'")
                label.label Medicamento
                MedicationSelect(
                  v-model="indication[data.label]"
                  @input="(val) => setMedication(val)"
                  :usesExternalMedication="usesExternalMedication"
                  @loadedMedications="loadedMedications = $event"
                )

              fieldset.modal__field(v-else-if="data.inputType === 'financiadorSelect'")
                label.label {{ data.friendlyName }}
                FinanciadorSelect(
                  v-model="indication[data.label]"
                  :isDisabled="isDisabledByRecetarioSolidario(data.label)"
                  :patientInsuranceName="patientInsuranceName"
                )

              fieldset.modal__field(v-else-if="data.inputType === 'checkbox'")
                label.label {{ data.friendlyName }}
                el-checkbox(
                  v-model="indication[data.label]",
                  :disabled="isPosting"
                )
              
              fieldset.modal__field(v-else-if="data.inputType === 'select'")
                label.label {{ data.friendlyName }}
                el-select(
                  v-model="indication[data.label]",
                  :disabled="isPosting",
                  placeholder="Seleccione una opción"
                )
                  el-option(
                    v-for="opt in data.options",
                    :key="opt.value",
                    :label="opt.label",
                    :value="opt.value"
                  )

              fieldset.modal__field(v-else-if="isQuantityField(data)")
                label.label {{ data.friendlyName }}
                ValidationProvider(
                  name="Cantidad",
                  rules="required",
                  v-slot="{ errors }"
                )
                  el-input(
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    v-model="indication.quantity"
                    :class="{ 'has-error': errors[0] || hasInvalidQuantity() }"
                    @keydown.native="preventInvalidQuantityKey($event)"
                  )
                  span.has-error(v-if="hasInvalidQuantity()") La cantidad debe ser mayor a 0.
                  span.has-error(v-else-if="errors[0]") {{ errors[0] }}
              
              fieldset.modal__field(v-else)
                label.label {{ data.friendlyName }}
                el-input(
                  :type="data.inputType",
                  v-model="indication[data.label]",
                  :value="data.defaultValue",
                  :disabled="isDisabledByRecetarioSolidario(data.label)"
                )
            .modal__row(
              v-if="indication.type === 'diagnosis' || indication.type === 'medications'"
            )
              fieldset.modal__field
                label.label Diagnóstico
                  DiagnosisSelect(
                    v-model="indication.diagnosis"
                  )
            .modal__row(
              v-if="theme === 'dash' && indication.type === 'medications'"
            )
              fieldset.modal__field
                label.label Firmar:
                input(
                  type="file",
                  ref="signatureFileUpload",
                  v-on:change="onSignatureChange"
                )

            .modal__row
              fieldset.modal__field
                label.label Archivos adjuntos (8 máx):
                file-upload(
                  ref="fileUpload",
                  :url="uploadMediaEndpoint",
                  :dropzone-options="getDropdownOptions()",
                  @file-added="onFileAdded",
                  @files-removed="onFileRemoved",
                  @queue-complete="close"
                )

  el-dialog(
    v-else,
    title="Indicación",
    custom-class="event-detail-modal",
    :visible.sync="isShowed",
    :before-close="close"
  )
    .view-task
      .metadata
        .form-item
          .description Tipo
          .response {{ currentIndication.name }}

        .form-item(
          v-if="indication.title && indication.type === 'medications'"
        )
          .description Título
          .response {{ indication.title }}

        .form-item(v-if="indication.description")
          .description Descripción
          .response {{ indication.description }}

        div(v-if="indicationForm")
          .form-item(v-if="indication.drugName")
            .description Nombre (Fármaco)
            .response {{ indication.drugName.name }}, {{ indication.drugName.presentation }}
          .form-item(v-if="indication.publicMedication")
            .description Nombre (Fármaco)
            .response {{ indication.publicMedication.name }}
          .form-item(v-if="indication.quantity")
            .description Cantidad
            .response {{ indication.quantity }}
          .form-item(v-if="indication.frequency")
            .description Frecuencia (hs)
            .response {{ indication.frequency }}
          .form-item(v-if="indication.duration")
            .description Plazo (días)
            .response {{ indication.duration }}
          .form-item(v-if="indication.observation")
            .description Observaciones
            .response {{ indication.observation }}
          .form-item(v-if="indication.dosage")
            .description Posología
            .response {{ indication.dosage }}
          .form-item(v-if="indication.isLongTerm")
            .description Largo plazo
            .response {{ indication.isLongTerm === 'S' ? 'Sí' : 'No' }}

        .form-item(v-if="indication.diagnosis")
          .description Diagnóstico
          .response {{ indication.diagnosis.description }}

        .form-item(v-if="indication.files.length")
          .description Archivos adjuntos:
            .attached-files
              .file(
                v-for="file in indicationFiles",
                :class="{ image: file.mimeType.includes('image') }"
              )
                a(:href="file.url", target="_blank", rel="noopener noreferrer")
                  img(
                    v-if="file.mimeType.includes('image')",
                    :src="file.url",
                    alt="Archivo adjunto"
                  )
                  span(v-else) {{ getFileName(file.key) }}

    span(slot="footer")
      button.button.button--blue(@click="close") Salir
</template>

<style lang="scss" scoped>
article {
  width: 100%;
}

.location {
  .micon {
    margin-right: 10px;
  }
}

.event-detail-modal {
  h2 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0 0 20px;
  }

  .form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;

    .description {
      word-break: break-word;
      font-weight: bold;
      color: $primary;
      margin-bottom: 10px;
    }

    .response {
      margin-left: 20px;
      display: list-item;
      list-style-type: "→ ";
    }

    .attached-files {
      display: flex;
      flex-wrap: wrap;
      .file {
        width: 100%;
        margin: 0 15px 15px 0;

        &:not(.image) a::before {
          content: "→ ";
        }
      }
      .file.image {
        width: 150px;
        height: 150px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
        box-shadow: 5px 4px 8px -3px rgba(0, 0, 0, 0.47);
        img {
          object-fit: cover;
          height: -webkit-fill-available;
        }
      }
    }
  }

  .view-task {
    padding: 1rem;
  }
}
</style>
